import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IProps } from './index'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { SwiperOptions, Navigation, Pagination } from 'swiper/core'
import { FeatureSpotlightTestimonial } from '../FeatureSpotlightTestimonial'

import { mq, mqBreakpoints } from 'marketing-site/src/library/utils'
import CarouselLeftArrow from 'marketing-site/src/images/carousel-left-arrow.svg'
import CarouselRightArrow from 'marketing-site/src/images/carousel-right-arrow.svg'
import CarouselLeftArrowLegacy from 'marketing-site/src/images/carousel-left-arrow-legacy.svg'
import CarouselRightArrowLegacy from 'marketing-site/src/images/carousel-right-arrow-legacy.svg'
import { useBrand } from '../../providers/brandProvider'
import useTranslate from '../../hooks/useTranslate'

const useCarouselArrows = () => {
  const brand = useBrand()
  if (brand === 'default') {
    return {
      left: CarouselLeftArrow,
      right: CarouselRightArrow,
    }
  }

  return {
    left: CarouselLeftArrowLegacy,
    right: CarouselRightArrowLegacy,
  }
}

export const FeatureSpotlightCarousel = ({ items }: IProps) => {
  const arrows = useCarouselArrows()
  const { translate } = useTranslate()

  const swiperRef = useRef<SwiperCore>()
  const prevRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)

  const [paginationTop, setPaginationTop] = useState(0)
  const [paginationRightTablet, setPaginationRightTablet] = useState(0)
  const [paginationLeftDesktop, setPaginationLeftDesktop] = useState(0)
  const [navigationLeftDesktop, setNavigationLeftDesktop] = useState(0)

  const breakpointDesktopPlus = 1200

  const setSwiperRef = useCallback((swiperInstance) => {
    if (swiperInstance) {
      swiperRef.current = swiperInstance
    }
  }, [])

  const settings: SwiperOptions = {
    autoHeight: true,
    spaceBetween: 24,
    navigation: {
      nextEl: nextRef.current,
      prevEl: prevRef.current,
    },
    pagination: {
      clickable: true,
    },
    loop: true,
  }

  SwiperCore.use([Navigation, Pagination])

  useEffect(() => {
    const swiperCurrentRef = swiperRef.current
    if (swiperCurrentRef) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // @ts-ignore
          setPaginationPosition(entry.target.swiper)
        }
      })
      resizeObserver.observe(swiperCurrentRef.$el[0])

      // Re-set swiper wrapper height on initialization.
      setTimeout(() => swiperRef.current?.updateAutoHeight(), 0)
    }
  }, [])

  const setPaginationPosition = (event: SwiperCore) => {
    if (!event || event.destroyed) return

    const activeIndex = event.activeIndex
    const backgroundBox: HTMLDivElement | null = event.slides[activeIndex].querySelector(
      '.feature-spotlight__background',
    )
    const mediaBox: HTMLDivElement | null = event.slides[activeIndex].querySelector(
      '.feature-spotlight__media',
    )
    const headingBox: HTMLHeadingElement | null = event.slides[activeIndex].querySelector(
      '.feature-spotlight__heading',
    )

    if (headingBox && document.body.clientWidth < mqBreakpoints.tablet) {
      setPaginationTop(headingBox.offsetTop + headingBox.offsetHeight + 16)
    }

    if (
      backgroundBox &&
      mediaBox &&
      document.body.clientWidth >= mqBreakpoints.tablet &&
      document.body.clientWidth < breakpointDesktopPlus
    ) {
      setPaginationRightTablet(backgroundBox.offsetLeft + mediaBox.offsetWidth)
    }

    if (backgroundBox && headingBox && document.body.clientWidth >= breakpointDesktopPlus) {
      setPaginationLeftDesktop(backgroundBox.offsetLeft + headingBox.offsetLeft)
      setNavigationLeftDesktop(backgroundBox.offsetLeft)
    }
  }

  return (
    <>
      <Swiper
        className="feature-spotlight-carousel"
        {...settings}
        onInit={(swiper) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line no-param-reassign
          swiper.params.navigation.prevEl = prevRef.current
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line no-param-reassign
          swiper.params.navigation.nextEl = nextRef.current
          swiper.navigation.update()
        }}
        onSwiper={(swiperInstance) => setSwiperRef(swiperInstance)}
        onSlideChange={(event) => setPaginationPosition(event)}
      >
        {items.map((item) => (
          <SwiperSlide key={item.heading}>
            <FeatureSpotlightTestimonial {...item} />
          </SwiperSlide>
        ))}
        <button className="swiper-button swiper-button-prev" ref={prevRef}>
          <span className="screen-readers-only">{translate('Previous slide')}</span>
        </button>
        <button className="swiper-button swiper-button-next" ref={nextRef}>
          <span className="screen-readers-only">{translate('Next slide')}</span>
        </button>
      </Swiper>
      <style jsx>
        {`
          :global(.feature-spotlight-carousel .swiper-wrapper) {
            will-change: transform, height;
          }

          :global(.feature-spotlight-carousel .swiper-pagination-bullet) {
            background: $ui-gray-legacy;
            opacity: 1;
          }

          :global(.feature-spotlight-carousel .swiper-pagination-bullet-active) {
            background: $black-legacy;
          }

          :global(.feature-spotlight-carousel .feature-spotlight__heading) {
            margin-bottom: 2.25rem;
          }

          :global(.feature-spotlight-carousel > .swiper-pagination) {
            top: ${paginationTop}px;
            bottom: auto;
            transition: top 0.3s ease;
          }

          .swiper-button {
            display: none;
            background-color: $light-gray-legacy;
            box-shadow: 0 0 0 2px $black-legacy;
            border-radius: 50%;
            height: 50px;
            width: 50px;

            &::after {
              font-size: inherit;
              line-height: 0;
            }
          }

          .swiper-button-prev {
            left: 4.1%;

            &::after {
              content: url(${arrows.left});
            }
          }

          .swiper-button-next {
            right: 4.1%;

            &::after {
              content: url(${arrows.right});
            }
          }

          .screen-readers-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }

          @media (${mq.tablet}) {
            :global(.feature-spotlight-carousel > .swiper-pagination) {
              width: auto;
              top: auto;
              right: ${paginationRightTablet}px;
              bottom: 5rem;
              left: auto;
            }
          }

          @media (${mq.desktop}) {
            :global(.feature-spotlight-carousel .feature-spotlight__lead),
            :global(.feature-spotlight-carousel .feature-spotlight__children) {
              max-width: 90%;
            }

            .swiper-button {
              display: flex;
            }

            .swiper-button-prev {
              left: ${navigationLeftDesktop}px;
              transform: translateX(-50%);
            }

            .swiper-button-next {
              right: ${navigationLeftDesktop}px;
              transform: translateX(50%);
            }
          }

          @media (min-width: ${breakpointDesktopPlus / 16}em) {
            :global(.feature-spotlight-carousel .feature-spotlight__lead),
            :global(.feature-spotlight-carousel .feature-spotlight__children) {
              max-width: 90%;
            }

            .swiper-button {
              display: flex;
            }

            .swiper-button-prev {
              left: ${navigationLeftDesktop}px;
              transform: translateX(-50%);
            }

            .swiper-button-next {
              right: ${navigationLeftDesktop}px;
              transform: translateX(50%);
            }

            :global(.feature-spotlight-carousel > .swiper-pagination) {
              right: auto;
              left: ${paginationLeftDesktop}px;
            }
          }

          // The following will only have effect in Safari
          _::-webkit-full-page-media,
          _:future,
          :global(:root .feature-spotlight-carousel .swiper-slide) {
            min-height: 100%;
          }
        `}
      </style>
    </>
  )
}
